import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import type { Event } from '../types/event';

export interface Filters {
  free: boolean;
  kidFriendly: boolean;
  hasDate: boolean;
  today: boolean;
  thisWeek: boolean;
  category: string | null;
}

export const DEFAULT_FILTERS: Filters = {
  free: false,
  kidFriendly: false,
  hasDate: false,
  today: false,
  thisWeek: false,
  category: null,
};

const KID_KEYWORDS = ['family', 'kid', 'child', 'children', 'youth', 'teen', 'all ages', 'family-friendly'];
const FREE_PATTERNS = ['free', '0', '0.00'];

function isFreeEvent(event: Event): boolean {
  if (!event.price) return false;
  const p = event.price.toLowerCase().trim();
  return FREE_PATTERNS.some(pat => p === pat) || p.startsWith('free');
}

function isKidFriendly(event: Event): boolean {
  const text = [
    event.title,
    event.description,
    ...(event.categories || []),
  ].filter(Boolean).join(' ').toLowerCase();
  return KID_KEYWORDS.some(kw => text.includes(kw));
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
}

function isThisWeek(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  return d >= startOfWeek && d < endOfWeek;
}

export function applyFilters(events: Event[], filters: Filters): Event[] {
  return events.filter((event) => {
    if (filters.free && !isFreeEvent(event)) return false;
    if (filters.kidFriendly && !isKidFriendly(event)) return false;
    if (filters.hasDate && !event.start_datetime) return false;
    if (filters.today && (!event.start_datetime || !isToday(event.start_datetime))) return false;
    if (filters.thisWeek && (!event.start_datetime || !isThisWeek(event.start_datetime))) return false;
    if (filters.category) {
      const cats = (event.categories || []).map(c => c.toLowerCase());
      if (!cats.some(c => c.includes(filters.category!.toLowerCase()))) return false;
    }
    return true;
  });
}

export function getActiveFilterCount(filters: Filters): number {
  let count = 0;
  if (filters.free) count++;
  if (filters.kidFriendly) count++;
  if (filters.hasDate) count++;
  if (filters.today) count++;
  if (filters.thisWeek) count++;
  if (filters.category) count++;
  return count;
}

function extractTopCategories(events: Event[], limit = 12): string[] {
  const counts: Record<string, number> = {};
  for (const event of events) {
    for (const cat of event.categories || []) {
      const normalized = cat.trim();
      if (normalized) counts[normalized] = (counts[normalized] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name]) => name);
}

export interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  allEvents: Event[];
}

interface ChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function Chip({ label, active, onClick }: ChipProps) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
        active
          ? 'bg-amber-500 text-zinc-950 shadow-sm shadow-amber-500/20'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
      }`}
    >
      {label}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
}

export default function FilterBar({ filters, onChange, allEvents }: FilterBarProps) {
  const [showCategories, setShowCategories] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const categoryBtnRef = useRef<HTMLButtonElement>(null);
  const activeCount = getActiveFilterCount(filters);
  const topCategories = extractTopCategories(allEvents);

  const toggle = (key: keyof Omit<Filters, 'category'>) => {
    const next = { ...filters, [key]: !filters[key] };
    if (key === 'today' && next.today) next.thisWeek = false;
    if (key === 'thisWeek' && next.thisWeek) next.today = false;
    onChange(next);
  };

  const setCategory = (cat: string | null) => {
    onChange({ ...filters, category: filters.category === cat ? null : cat });
    setShowCategories(false);
  };

  const clearAll = () => onChange(DEFAULT_FILTERS);

  const openDropdown = () => {
    if (categoryBtnRef.current) {
      const rect = categoryBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 6, left: rect.left });
    }
    setShowCategories(true);
  };

  // Close on scroll/resize so position doesn't drift
  useEffect(() => {
    if (!showCategories) return;
    const close = () => setShowCategories(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [showCategories]);

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 overflow-x-auto flex-shrink-0 scrollbar-none">
      <div className="flex items-center gap-1 text-slate-400 flex-shrink-0 mr-1">
        <SlidersHorizontal className="w-3.5 h-3.5" />
      </div>

      <Chip label="Free" active={filters.free} onClick={() => toggle('free')} />
      <Chip label="Kid Friendly" active={filters.kidFriendly} onClick={() => toggle('kidFriendly')} />
      <Chip label="Today" active={filters.today} onClick={() => toggle('today')} />
      <Chip label="This Week" active={filters.thisWeek} onClick={() => toggle('thisWeek')} />
      <Chip label="Has Date" active={filters.hasDate} onClick={() => toggle('hasDate')} />

      {/* Category button — dropdown rendered in portal to escape overflow clipping */}
      <button
        ref={categoryBtnRef}
        onClick={openDropdown}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
          filters.category
            ? 'bg-amber-500 text-zinc-950 shadow-sm shadow-amber-500/20'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
        }`}
      >
        {filters.category || 'Category'}
        <ChevronDown className={`w-3 h-3 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
      </button>

      {activeCount > 0 && (
        <button
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition-all whitespace-nowrap flex-shrink-0"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      )}

      {showCategories && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowCategories(false)} />
          <div
            className="fixed z-50 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 min-w-[180px] animate-fade-in-up"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {filters.category && (
              <button
                onClick={() => setCategory(null)}
                className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors font-medium"
              >
                Clear category
              </button>
            )}
            {topCategories.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-400">No categories found</p>
            )}
            {topCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                  filters.category === cat
                    ? 'bg-amber-50 text-amber-700 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
